import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import type { Dispatch } from "@reduxjs/toolkit";
import { generateApiKey, refreshSession } from "api/SessionAuth";
import { clearAuthSession } from "utils/auth/clearAuthSession";
import {
  adoptUserApiKey,
  API_KEY_STORAGE_KEY,
  getStoredApiKey,
  isUserApiKey,
  storeUserApiKey,
} from "utils/auth/apiKeyStore";
import { SIGN_IN_PATH } from "utils/auth/signInPath";

/**
 * 401 api-key recovery for data calls (spec 2026-10-20-website-api-settings,
 * Stage 5.2).
 *
 * Data calls carry `Bearer <api key>`; the login session is a separate cookie
 * pair. When the key is rotated in another tab or browser, this tab's key
 * starts answering 401 while its session is still alive. The interceptor asks
 * the auth-service for the account's current key (refreshing the session
 * once if needed), stores it everywhere, and replays the failed request once.
 *
 * A rotation in another tab of the same browser arrives as a `storage` event:
 * this tab adopts the new key at once and records the swap, so a request it
 * already sent with the old key is replayed rather than failed.
 *
 * It acts ONLY on a 401 for a request that carried THIS user's stored key:
 * anonymous calls (default key), auth-service calls and 403s pass through.
 *
 * The local session is ended (cleared + redirect to sign-in) ONLY when the
 * auth-service itself rejects the session: refresh/generate answer 401 or 403.
 * A 5xx, a network error, a timeout or a malformed reply is a blip, not a
 * verdict on the session: the original request is rejected and the user stays
 * signed in, so an auth-service outage cannot mass-log-out everyone.
 */

export { SIGN_IN_PATH };

/** `REACT_APP_KEY_RECOVERY=false` turns recovery off (local dev: cookies are cross-site). */
export const isKeyRecoveryEnabled = (): boolean =>
  (process.env.REACT_APP_KEY_RECOVERY ?? "").trim().toLowerCase() !== "false";

/**
 * True when the auth-service refused the session itself (401/403), as opposed
 * to failing to answer (5xx, network, timeout) or answering garbage.
 */
export const isSessionRejected = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === 401 || status === 403;
};

/** generate → on 401, refresh once → generate. Any other failure is final. */
export const resolveSessionApiKey = async (): Promise<string> => {
  try {
    return await generateApiKey();
  } catch (error) {
    if (!axios.isAxiosError(error) || error.response?.status !== 401) {
      throw error;
    }
  }
  await refreshSession();
  return generateApiKey();
};

export interface KeyRecoveryOptions {
  /** Redux dispatch, injected at bootstrap so this module never imports the store. */
  dispatch: Dispatch;
  /** Called once when the auth-service rejects the session (401/403), after the local session is cleared. */
  redirectToSignIn?: () => void;
  /** Feature flag; read per 401 so it can be flipped without reinstalling. */
  isEnabled?: () => boolean;
  /** Source of the account's current key. */
  resolveKey?: () => Promise<string>;
}

// Marks a replayed request so its own 401 is never recovered again.
const REPLAY_FLAG = "__apiKeyRecoveryReplay";
type RecoverableConfig = InternalAxiosRequestConfig & {
  [REPLAY_FLAG]?: boolean;
};

const bearerOf = (config: InternalAxiosRequestConfig): string | null => {
  const header = AxiosHeaders.from(config.headers).get("Authorization");
  if (typeof header !== "string") return null;
  const match = /^Bearer\s+(.+)$/i.exec(header.trim());
  return match ? match[1].trim() : null;
};

const isAuthServiceUrl = (config: InternalAxiosRequestConfig): boolean => {
  const url = config.url ?? "";
  try {
    const base = config.baseURL || window.location.origin;
    return new URL(url, base.endsWith("/") ? base : `${base}/`).pathname.startsWith(
      "/auth/"
    );
  } catch {
    return /(^|\/)auth\//.test(url);
  }
};

const withBearer = (
  config: RecoverableConfig,
  key: string
): RecoverableConfig => {
  const headers = AxiosHeaders.from(config.headers);
  headers.set("Authorization", `Bearer ${key}`);
  return { ...config, headers, [REPLAY_FLAG]: true };
};

/**
 * Register the recovery interceptor on `instance`. Returns an uninstall
 * function. All recovery state (in-flight promise, last rotation, ended
 * session) lives in this closure, one per installation.
 */
export const installApiKeyRecovery = (
  instance: AxiosInstance,
  {
    dispatch,
    redirectToSignIn = () => window.location.assign(SIGN_IN_PATH),
    isEnabled = isKeyRecoveryEnabled,
    resolveKey = resolveSessionApiKey,
  }: KeyRecoveryOptions
): (() => void) => {
  let inFlight: Promise<string> | null = null;
  // The last successful swap. A request sent with the old key that fails
  // AFTER the swap landed is replayed with the new key, not recovered again.
  let lastSwap: { from: string; to: string } | null = null;
  let sessionEnded = false;

  const endSession = () => {
    if (sessionEnded) return;
    sessionEnded = true;
    clearAuthSession();
    redirectToSignIn();
  };

  // Single-flight: every concurrent 401 awaits the same recovery.
  const recover = (staleKey: string): Promise<string> => {
    if (!inFlight) {
      inFlight = resolveKey()
        .then((key) => {
          storeUserApiKey(dispatch, key);
          lastSwap = { from: staleKey, to: key };
          return key;
        })
        .catch((error) => {
          if (isSessionRejected(error)) endSession();
          throw error;
        })
        .finally(() => {
          inFlight = null;
        });
    }
    return inFlight;
  };

  const onRejected = async (error: AxiosError) => {
    const config = error?.config as RecoverableConfig | undefined;
    if (
      !config ||
      error.response?.status !== 401 ||
      config[REPLAY_FLAG] ||
      sessionEnded ||
      !isEnabled() ||
      isAuthServiceUrl(config)
    ) {
      throw error;
    }
    const sentKey = bearerOf(config);
    if (!isUserApiKey(sentKey)) throw error;

    const storedKey = getStoredApiKey();
    let freshKey: string;
    if (sentKey === storedKey) {
      try {
        freshKey = await recover(sentKey);
      } catch {
        throw error;
      }
    } else if (lastSwap && sentKey === lastSwap.from && storedKey === lastSwap.to) {
      freshKey = lastSwap.to;
    } else {
      // Not this user's current key (e.g. an explicit admin/other key): not ours.
      throw error;
    }
    return instance.request(withBearer(config, freshKey));
  };

  const onStorage = (event: StorageEvent) => {
    if (event.key !== API_KEY_STORAGE_KEY || !isUserApiKey(event.newValue)) return;
    adoptUserApiKey(dispatch, event.newValue);
    if (isUserApiKey(event.oldValue) && event.oldValue !== event.newValue) {
      lastSwap = { from: event.oldValue, to: event.newValue };
    }
  };

  const id = instance.interceptors.response.use(
    (response) => response,
    onRejected
  );
  window.addEventListener("storage", onStorage);
  return () => {
    instance.interceptors.response.eject(id);
    window.removeEventListener("storage", onStorage);
  };
};
